import React, { useState, useMemo, useEffect, useRef } from 'react';
import { styled, useTheme, alpha } from '@mui/material/styles';
import Autocomplete from '@mui/material/Autocomplete';
import MuiLink from '@mui/material/Link';
import DoneIcon from '@mui/icons-material/Done';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import Box from '@mui/material/Box';
import ListItemText from '@mui/material/ListItemText';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Typography from '@mui/material/Typography';
import Popper from '@mui/material/Popper';
import { createFilterOptions } from '@mui/material/useAutocomplete';
import InputBase from '@mui/material/InputBase';
import ListItem from '@mui/material/ListItem';
const filter = createFilterOptions();
const ADD_PREFIX = 'Add "';
const OPERATORS = new Set(['AND', 'OR', 'WITH']);

const StyledPopper = styled(Popper)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: `0 8px 24px ${alpha(theme.palette.neutral[900], 0.2)}`,
  borderRadius: 6,
  width: 300,
  zIndex: theme.zIndex.modal,
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.paper
}));

const TriggerContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '100%'
}));

const StyledTriggerInput = styled(InputBase)(({ theme }) => ({
  padding: `8.5px 6px 8.5px ${theme.spacing(1.75)}`,
  justifyContent: 'flex-start',
  borderRadius: theme.spacing(0.5),
  display: 'flex',
  alignItems: 'center',
  height: theme.spacing(5),
  width: '100%',
  textAlign: 'left',
  paddingRight: '50px',
  border: `1px solid ${theme.palette.input.border}`,

  '&:hover': {
    borderColor: theme.palette.text.primary
  },

  '&.Mui-focused': {
    outline: 'none',
    borderColor: theme.palette.mode === 'light' ? theme.palette.neutral[700] : theme.palette.blue.link,
    boxShadow: `${theme.palette.mode === 'light' ? theme.palette.neutral[700] : theme.palette.blue.link} 0 0 0 1px inset`
  },

  '& input': {
    padding: 0
  }
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.selectedBackground.selector
  }
}));

const NRAutocompleteFilter = ({
  value,
  setValue,
  options,
  placeholder = 'Select...',
  filterPlaceholder = 'Filter',
  showMultipleLicensesWarning = false
}) => {
  const [open, setOpen] = useState(false);
  const [invalidLicense, setInvalidLicense] = useState(null);
  const [inputValue, setInputValue] = useState(value || '');
  const inputRef = useRef(null);
  const theme = useTheme();

  const optionSet = useMemo(() => new Set(options || []), [options]);

  useEffect(() => {
    if (!open) {
      setInputValue(value || '');
    }
  }, [value, open]);

  const sortedOptions = useMemo(() => {
    const out = [...(options || [])];
    out.sort((a, b) => a.localeCompare(b));

    if (!open) return out;

    const result = [];
    let selected = null;

    for (const option of out) {
      if (option === value) selected = option;
      else result.push(option);
    }

    return selected != null ? [selected, ...result] : result;
  }, [options, value, open]);

  const handleClose = () => {
    setOpen(false);
  };

  const isInputInvalid = v => {
    if (!v || typeof v !== 'string') {
      return false;
    }

    const normalized = v.replace(/[()]/g, ' ');
    const tokens = normalized.trim().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return false;

    const allValidTokens = tokens.every(token => optionSet.has(token) || OPERATORS.has(token));
    if (!allValidTokens) return true;

    // Check if it ends with an operator
    const lastToken = tokens[tokens.length - 1];
    if (OPERATORS.has(lastToken)) return true;

    return false;
  };

  const handleSetValue = v => {
    setValue(v);

    if (!v) {
      setInvalidLicense(false);
      return;
    }

    setInvalidLicense(isInputInvalid(v));
  };

  return (
    <>
      <Box sx={{ width: '100%', fontSize: 16 }}>
        <ClickAwayListener onClickAway={handleClose}>
          <Box>
            <Autocomplete
              open={open}
              onOpen={() => setOpen(true)}
              onClose={() => {
                handleClose();
              }}
              multiple={false}
              value={value}
              inputValue={inputValue}
              onInputChange={(event, newInputValue, reason) => {
                let valueToUse = newInputValue;
                if (newInputValue?.startsWith(ADD_PREFIX)) {
                  valueToUse = newInputValue.substring(ADD_PREFIX.length, newInputValue.length - 1);
                }
                setInputValue(valueToUse || '');
                if (reason === 'input' && !open) setOpen(true);
              }}
              onChange={(event, newValue) => {
                let valueToUse = newValue;
                if (newValue?.startsWith(ADD_PREFIX)) {
                  valueToUse = newValue.substring(ADD_PREFIX.length, newValue.length - 1);
                }
                handleSetValue(valueToUse);
                setInputValue(valueToUse || '');
                handleClose();
              }}
              disableCloseOnSelect={false}
              disableListWrap
              options={sortedOptions}
              PopperComponent={StyledPopper}
              renderTags={() => null}
              noOptionsText="No options"
              filterOptions={(options, params) => {
                const filtered = filter(options, params);

                const { inputValue } = params;
                const isExisting = optionSet.has(inputValue) || inputValue === value;

                if (inputValue !== '' && !isExisting && !inputValue.startsWith(ADD_PREFIX)) {
                  filtered.unshift(`${ADD_PREFIX}${inputValue}"`);
                }

                return filtered;
              }}
              isOptionEqualToValue={(option, val) => option === val}
              renderOption={(props, option) => {
                const isSelected = value === option;
                return (
                  <StyledListItem {...props} key={option}>
                    <Box
                      component={DoneIcon}
                      sx={{
                        width: 17,
                        height: 17,
                        margin: '7px 5px -2px'
                      }}
                      style={{
                        visibility: isSelected ? 'visible' : 'hidden'
                      }}
                    />
                    <Box
                      sx={{
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        flexGrow: 1,
                        '& p': {
                          color: theme.palette.text.primary
                        }
                      }}
                    >
                      <ListItemText primary={option} />
                    </Box>
                  </StyledListItem>
                );
              }}
              renderInput={params => (
                <TriggerContainer>
                  <StyledTriggerInput
                    {...params.InputProps}
                    inputRef={inputRef}
                    inputProps={params.inputProps}
                    placeholder={open ? filterPlaceholder : placeholder}
                    onFocus={() => setOpen(true)}
                    onClick={() => setOpen(true)}
                  />
                  {/*{!!value && (
                    <IconButton
                      size="small"
                      sx={{ position: 'absolute', right: 30, top: '50%', transform: 'translateY(-50%)' }}
                      onClick={handleOnClear}
                    >
                      <ClearIcon />
                    </IconButton>
                  )}
                  <ArrowDropDown
                    sx={{
                      position: 'absolute',
                      right: 6,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none',
                      color: theme.palette.text.iconSecondary,
                      ...(open && { transform: 'translateY(-50%) rotate(180deg)' })
                    }}
                  />*/}
                </TriggerContainer>
              )}
            />
          </Box>
        </ClickAwayListener>

        {showMultipleLicensesWarning && (
          <Box sx={{ mt: 0.5, ml: 2 }} gap={1} display={'flex'} flexDirection={'row'} alignItems={'center'}>
            <WarningAmberOutlinedIcon
              sx={{ width: theme.spacing(2), height: theme.spacing(2), color: theme.palette.warning.main, mt: theme.spacing(0.25) }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Warning: Selecting a new license from this list will overwrite and replace existing ones.
            </Typography>
          </Box>
        )}

        {!!value && invalidLicense && (
          <Box sx={{ mt: 0.5, ml: 2 }} gap={1} display="flex" flexDirection="row">
            <WarningAmberOutlinedIcon
              sx={{ width: theme.spacing(2), height: theme.spacing(2), color: theme.palette.warning.main, mt: theme.spacing(0.5) }}
            />
            <Box>
              <Typography variant="caption" color="text.secondary">
                This license or expression is not valid. Submission is allowed, but it should follow the guidelines below:
              </Typography>
              <Box component="ul" sx={{ pl: 2, mt: 0.25, mb: 0 }}>
                <Typography component="li" variant="caption" color="text.secondary">
                  Use SPDX-compliant identifiers from the
                  <MuiLink
                    href="https://spdx.org/licenses/"
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="none"
                    sx={{
                      color: theme.palette.blue.link,
                      textDecoration: 'none',
                      '&:visited': {
                        color: theme.palette.blue.link
                      }
                    }}
                  >
                    <Typography component="span" sx={{ pl: 0.5 }} variant="caption">
                      official list
                    </Typography>
                  </MuiLink>
                  .
                </Typography>
                <Typography component="li" variant="caption" color="text.secondary">
                  Operators must be AND, OR, or WITH.
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

export default NRAutocompleteFilter;
